import '../../product/product.scss';
import { ReactNode, useContext, useState } from 'react';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { Product, ProductListRequest, ProductUpdateRequest } from '../../../clients/quipli-service/product/product';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { ActivateAction, DeactivateAction, DeleteAction, EditAction } from '../../../components/actions/actions';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Button } from '../../../components/form/button';
import { SelectForm } from '../../../components/select-form/select-form';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { toast } from '../../../util/toasts';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { ProductPrice, SaleProductPrice } from '../../../clients/quipli-service/pricing/pricing';
import { Dictionary, groupBy } from 'lodash';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { useProductApiV1 } from '../../../clients/quipli-service/product/product-api-v1';
import Icon from '../../../components/icons/icon';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';
import { LocationSelect } from '../../location/select/location-select';
import { TenantContext } from '../../../context/tenant-context';
import { BreadCrumb } from '../../../components/breadcrumbs/breadcrumbs';
import { useImportExport } from '../../../components/import-export/use-import-export';
import { useServicesImportExportAdapter } from './services-adapter';

export const ServiceList = () => {
  const api = useAPI();
  const { listPrices } = usePricingApiV1();
  const productAPI = useProductApiV1();

  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [actionModal, setActionModal] = useState<ReactNode>();
  const { contextPath } = useContext(TenantContext);
  const [pagination, setPagination] = useState<PaginationData>();

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<ProductListRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      locationId: params.has('locationId') ? parseInt(params.get('locationId')!) : undefined,
      isActive: params.has('isActive') ? params.get('isActive')! === 'true' : undefined,
      createdFrom: params.has('createdFrom') ? DateTime.fromISO(params.get('createdFrom')!) : undefined,
      createdTo: params.has('createdTo') ? DateTime.fromISO(params.get('createdTo')!) : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setListLoading(true);

      const { keyword, locationId, isActive, createdFrom, createdTo, pageSize, pageNumber } = _request;
      productAPI
        .searchServices({
          keyword,
          locationIds: locationId ? [locationId] : undefined,
          isActive: isActive,
          createdFrom: createdFrom?.toISODate(),
          createdTo: createdTo?.toISODate(),
          pageSize,
          pageNumber,
        })
        .then(({ pagination, items }) => {
          setPagination(pagination);
          listPrices({
            productIds: items.map((i) => i.id),
            locationId: _request.locationId,
            type: OrderType.Sale,
          }).then((prices) => {
            const pricesMap: Dictionary<ProductPrice[]> = groupBy(prices, 'productId');

            setData(items.map((item) => buildDataRow(item, pricesMap[item.id] ?? [])));
            setListLoading(false);
          });
        })
        .catch(() => {});
    },
  );

  const servicesAdapter = useServicesImportExportAdapter(fetchCallback);
  const { ImportExportToolbar } = useImportExport(servicesAdapter, {}, {});

  // Vars
  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'salePrice', label: 'Sale Price' },
    { key: 'date', label: 'Date' },
    { key: 'buttons', label: 'Actions', fixedAsLast: true },
  ];

  const productStatusValues: { label: string; value: 'Yes' | 'No' }[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];

  // Callbacks
  async function doUpdate(i: number, update: ProductUpdateRequest) {
    const productId = i;
    await api
      .updateProduct(productId, update)
      .then(() => {
        toast('Successfully updated!', 'success');
        fetchCallback();
      })
      .catch((err) => {
        const errMsg = err.status === 400 && err.message ? err.message.msg : 'Error updating service!';
        toast(errMsg, 'error');
      });
  }

  //Helpers
  const buildDataRow = (p: Product, prices: ProductPrice[]) => {
    const salePriceUnits = prices.filter((p) => p.type === OrderType.Sale) as SaleProductPrice[];

    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {p.name}
          </Typography>
        </>
      ),
      fields: {
        name: (
          <div className="product-container">
            <Typography type="text-sm" className="text-[#1D252C]">
              {p.name}
            </Typography>
          </div>
        ),
        salePrice: buildListPrices(salePriceUnits, { priceColor: 'text-[#1D252C]', unitColor: 'text-[#667085]' }),
        date: (
          <>
            <div>
              <Typography type="text-sm" className="text-[#1D252C]">
                {p.createdAt.toFormat('LLL-dd')}
              </Typography>
              <Typography type="text-sm" className="text-[#1D252C]">
                {p.createdAt.toFormat('hh:mm a').toLowerCase()}
              </Typography>
            </div>
          </>
        ),
        buttons: (
          <div className="button-group">
            {p.isActive ? (
              <DeactivateAction
                onClick={() => doUpdate(p.id, { isActive: false, isDeleted: p.isDeleted })}
                data-cy="hide_service"
              />
            ) : (
              <ActivateAction
                onClick={() => doUpdate(p.id, { isActive: true, isDeleted: p.isDeleted })}
                data-cy="show_service"
              />
            )}
            <EditAction
              onClick={() => {
                const url = `${contextPath}manager/inventory/services/form/${p.id}`;
                window.open(url, '_self')?.focus();
              }}
              data-cy="edit_service"
            />
            <DeleteAction
              onClick={() =>
                setActionModal(
                  <ConfirmModal
                    isOpen={true}
                    close={() => setActionModal(undefined)}
                    onConfirm={() => {
                      doUpdate(p.id, { isActive: p.isActive, isDeleted: true });
                      setActionModal(undefined);
                    }}
                    headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
                    prompt={`Are you sure you want to delete service ${p.name}?`}
                  />,
                )
              }
              data-cy="delete_service"
            ></DeleteAction>
          </div>
        ),
        id: p.id,
      },
      /*redirectTo: `products/form/${p.id}` Hide until there is a service management page */
    };
  };

  const buildListPrices = (prices: SaleProductPrice[], colors: { priceColor: string; unitColor: string }) => {
    const priceRates: any = [];

    prices.forEach((price) => {
      const priceRate = {
        prod_id: price.productId,
        price: (price.price / 100).toFixed(2),
      };
      priceRates.push(priceRate);
    });

    return (
      <div>
        {priceRates.length > 0 ? (
          <ul className="product-prices">
            {priceRates.map((priceRate: any) => (
              <li>
                <span className={`quipli-v2 text-sm regular ${colors.priceColor}`}>{`$${priceRate.price}`}</span>{' '}
                {priceRate.show_unit && <span className={colors.unitColor}>{priceRate.desc}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p>N/A</p>
        )}
      </div>
    );
  };

  const AddService = () => (
    <Button
      className="button-yellow md w-[max-content]"
      data-cy="add_services"
      onClick={() => {
        const url = `${contextPath}manager/inventory/services/form`;
        window.open(url, '_self')?.focus();
      }}
    >
      <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
        Add Service
      </Typography>
      <Icon icon="Plus" size="medium" stroke="var(--quipli-black-800)" />
    </Button>
  );

  return (
    <div className="product-list">
      <div className="container-v2">
        <BreadCrumb breadcrumbs={[{ label: 'Inventory' }, { label: 'Services' }]} />

        <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4">
          <Typography type="display-sm" weight="medium">
            Services
          </Typography>
          <ImportExportToolbar>
            <AddService />
          </ImportExportToolbar>
        </div>

        <div className="flex items-start sm:items-center justify-between search-bar-padding flex-col sm:flex-row">
          <SearchBar
            placeholder="Search for a service"
            defaultValue={request.keyword}
            setRequest={setRequest}
            data-cy="search_services"
            className="w-full"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 py-5 [&>*]:shrink-0">
          <div>
            <LocationSelect
              setLocation={(l) => {
                setRequest((prevState) => ({ ...prevState, locationId: l?.id }));
              }}
              clearable={true}
              dataCy="location-select"
            />
          </div>
          <div>
            <SelectForm
              options={productStatusValues}
              onChange={(ps) => {
                setRequest((prevState) => ({ ...prevState, isActive: ps ? ps.value === 'Yes' : undefined }));
              }}
              isClearable={true}
              placeholder="Active"
              value={
                request.isActive
                  ? productStatusValues.find(
                      (e) => (e.value === 'Yes' && request.isActive) || (e.value === 'No' && !request.isActive),
                    )
                  : undefined
              }
            />
          </div>
          <div className="date-picker-filter">
            <DatePicker
              value={request.createdFrom}
              onChange={(from) => setRequest({ ...request, createdFrom: from })}
              placeholder="Date From"
              clearable
            />
          </div>
          <div className="date-picker-filter">
            <DatePicker
              value={request.createdTo}
              onChange={(to) => setRequest({ ...request, createdTo: to })}
              placeholder="Date To"
              clearable
            />
          </div>
        </div>

        <DataTable
          isLoading={isListLoading}
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          cypressPrevious="previous_services_page"
          cypressNext="next_services_page"
        />

        {actionModal}
      </div>
    </div>
  );
};
